import React, { useContext, useEffect, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Spinner } from "react-bootstrap";
import { useSyncedField } from "../../components/Sidebar";
import SidebarContext from "../../components/Sidebar/SidebarContext";
import StarekContext from "../../context/StarekContext";

const ENTITY_TYPES_QUERY = gql`
    query EntityTypesForWebhook {
        entityTypes {
            id
            code
            singular
            plural
            type
            is_active
        }
    }
`;

const WEBHOOK_BIT = 5;
const ACTION_BITS = { create: 0, update: 1, delete: 2 };
const ACTIONS = ["create", "update", "delete"];

function isBitSet(value, bit) {
    return (value & (1 << bit)) !== 0;
}

function buildSubscriptionsFromMatrix(matrix) {
    const result = [];
    for (const [entityTypeId, actions] of Object.entries(matrix)) {
        let mask = 0;
        if (actions.create) mask |= 1 << ACTION_BITS.create;
        if (actions.update) mask |= 1 << ACTION_BITS.update;
        if (actions.delete) mask |= 1 << ACTION_BITS.delete;
        if (mask > 0) {
            result.push({ entity_type_id: Number(entityTypeId), action_mask: mask });
        }
    }
    return result;
}

function buildMatrixFromSubscriptions(subscriptions, entityTypes) {
    const matrix = {};
    for (const et of entityTypes) {
        matrix[et.id] = { create: false, update: false, delete: false };
    }
    if (!subscriptions) return matrix;
    for (const sub of subscriptions) {
        if (matrix[sub.entity_type_id]) {
            matrix[sub.entity_type_id] = {
                create: isBitSet(sub.action_mask, ACTION_BITS.create),
                update: isBitSet(sub.action_mask, ACTION_BITS.update),
                delete: isBitSet(sub.action_mask, ACTION_BITS.delete),
            };
        }
    }
    return matrix;
}

function ToggleSwitch({ id, checked, onChange }) {
    return (
        <div className="form-check form-switch mb-0 d-flex justify-content-center">
            <input
                id={id}
                type="checkbox"
                className="form-check-input"
                role="switch"
                checked={checked}
                onChange={onChange}
                style={{
                    backgroundColor: checked ? "#0d6efd" : "#6c757d",
                    borderColor: checked ? "#0d6efd" : "#6c757d",
                    width: "2.5rem",
                    height: "1.25rem",
                    cursor: "pointer",
                }}
            />
        </div>
    );
}

function SelectAllButton({ allOn, onClick }) {
    return (
        <button
            type="button"
            className={`btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center ${allOn ? "btn-primary" : "btn-dark"}`}
            title={allOn ? "Clear all" : "Select all"}
            onClick={onClick}
            style={{
                width: "26px",
                height: "26px",
                padding: 0,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            <i className="fas fa-check-double" style={{ fontSize: "0.6rem" }} />
        </button>
    );
}

const TableStyles = () => (
    <style>{`
      .event-table { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; }
      .event-table col.resource { width: auto; }
      .event-table col.action { width: 100px; }
      .event-table col.row-action { width: 36px; }
      .event-table thead th { padding: 8px; font-weight: 600; color: #212529; font-size: 0.85rem; }
      .event-table tbody td { padding: 8px; border-top: 1px solid #eee; vertical-align: middle; }
      .event-table th.action, .event-table td.action { text-align: center; }
      .event-table td.row-action { text-align: center; }
    `}</style>
);

const SectionTitle = ({ children }) => (
    <div className="d-flex align-items-center mt-1 mb-2">
        <h6 className="mb-0 text-uppercase text-muted">{children}</h6>
        <hr className="flex-grow-1 ms-2 my-1" />
    </div>
);

const WebhookEvents = () => {
    const starekContext = useContext(StarekContext);
    const { entity } = useContext(SidebarContext);
    const [, setSubscriptions] = useSyncedField("subscriptions", []);

    const { data, loading } = useQuery(ENTITY_TYPES_QUERY, {
        client: starekContext.engineClient,
    });

    const webhookEntityTypes = (data?.entityTypes || [])
        .filter((et) => et.is_active && isBitSet(et.type, WEBHOOK_BIT))
        .sort((a, b) => (a.singular || a.code).localeCompare(b.singular || b.code));

    const [matrix, setMatrix] = useState({});

    useEffect(() => {
        if (webhookEntityTypes.length === 0) return;
        const existing = entity?.subscriptions || [];
        setMatrix(buildMatrixFromSubscriptions(existing, webhookEntityTypes));
    }, [entity?.id, webhookEntityTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSubscriptions(buildSubscriptionsFromMatrix(matrix));
    }, [matrix]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (entityTypeId, action) => {
        setMatrix((prev) => ({
            ...prev,
            [entityTypeId]: {
                ...prev[entityTypeId],
                [action]: !prev[entityTypeId]?.[action],
            },
        }));
    };

    const toggleAllForRow = (entityTypeId) => {
        const current = matrix[entityTypeId] || {};
        const allOn = ACTIONS.every((a) => current[a]);
        setMatrix((prev) => ({
            ...prev,
            [entityTypeId]: Object.fromEntries(ACTIONS.map((a) => [a, !allOn])),
        }));
    };

    const toggleAllForAction = (action) => {
        const allOn = webhookEntityTypes.every((et) => matrix[et.id]?.[action]);
        setMatrix((prev) => {
            const next = { ...prev };
            for (const et of webhookEntityTypes) {
                next[et.id] = { ...next[et.id], [action]: !allOn };
            }
            return next;
        });
    };

    if (loading) {
        return (
            <div className="p-3 text-center">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    return (
        <div className="p-3">
            <TableStyles />
            <SectionTitle>Event Subscriptions</SectionTitle>
            <p className="text-muted small mb-3">
                Select which entity types and actions should trigger this webhook.
            </p>
            <table className="event-table">
                <colgroup>
                    <col className="resource" />
                    <col className="action" />
                    <col className="action" />
                    <col className="action" />
                    <col className="row-action" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Entity</th>
                        {ACTIONS.map((action) => {
                            const allOn = webhookEntityTypes.length > 0 &&
                                webhookEntityTypes.every((et) => matrix[et.id]?.[action]);
                            return (
                                <th key={action} className="action">
                                    <div className="d-flex flex-column align-items-center gap-1">
                                        <span style={{ textTransform: "capitalize" }}>{action}</span>
                                        <SelectAllButton
                                            allOn={allOn}
                                            onClick={() => toggleAllForAction(action)}
                                        />
                                    </div>
                                </th>
                            );
                        })}
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {webhookEntityTypes.map((et) => {
                        const allOn = ACTIONS.every((a) => matrix[et.id]?.[a]);
                        return (
                            <tr key={et.id}>
                                <td>{et.singular || et.plural || et.code}</td>
                                {ACTIONS.map((action) => (
                                    <td key={action} className="action">
                                        <ToggleSwitch
                                            id={`${et.id}-${action}`}
                                            checked={!!matrix[et.id]?.[action]}
                                            onChange={() => toggle(et.id, action)}
                                        />
                                    </td>
                                ))}
                                <td className="row-action">
                                    <SelectAllButton
                                        allOn={allOn}
                                        onClick={() => toggleAllForRow(et.id)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                    {webhookEntityTypes.length === 0 && (
                        <tr>
                            <td colSpan={5} className="text-muted text-center py-3">
                                No webhook-enabled entity types found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default WebhookEvents;
